import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { SignalRadar } from './SignalRadar';
import { EASE } from './animations';

/**
 * The hero's right-column product mock. USER-DRIVEN tabs (地図 / シグナル /
 * 敬語) — deliberately NOT an autoplay cross-fade: above-the-fold auto-motion
 * reads 軽い to a JP B2B buyer, and a static-first hero respects 余白/restraint
 * (TOPPAGE_REDESIGN.md → Motion budget). The radar's own sweep is the one
 * sanctioned ambient loop here.
 */
type Face = 'map' | 'signals' | 'keigo';

export function HeroMock() {
  const { t } = useTranslation();
  const reduced = useReducedMotion();
  const [face, setFace] = useState<Face>('map');

  const tabs: Array<{ id: Face; label: string }> = [
    { id: 'map', label: t('hero.mock_tab_map') },
    { id: 'signals', label: t('hero.mock_tab_signals') },
    { id: 'keigo', label: t('hero.mock_tab_keigo') },
  ];

  return (
    <div className="relative">
      {/* Face */}
      <div className="relative min-h-[300px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={face}
            initial={reduced ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.35, ease: EASE }}
          >
            {face === 'map' && <SignalRadar />}
            {face === 'signals' && <SignalFace />}
            {face === 'keigo' && <KeigoFace />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* User-driven tab rail */}
      <div
        role="tablist"
        aria-label={t('hero.mock_aria')}
        className="mt-4 flex items-center justify-center gap-1 rounded-lg border border-ink-200 bg-white/70 p-1 backdrop-blur"
      >
        {tabs.map((tb) => {
          const active = tb.id === face;
          return (
            <button
              key={tb.id}
              role="tab"
              aria-selected={active}
              onClick={() => setFace(tb.id)}
              className={
                'relative flex-1 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ' +
                (active ? 'text-white' : 'text-ink-600 hover:text-ink-900')
              }
            >
              {active && (
                <motion.span
                  layoutId="hero-mock-pill"
                  className="absolute inset-0 rounded-md bg-ink-900"
                  transition={{ duration: 0.25, ease: EASE }}
                />
              )}
              <span className="relative z-10">{tb.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Signal-feed face — 法人番号-matched account + typed signals (illustrative). */
function SignalFace() {
  const { t } = useTranslation();
  return (
    <svg
      viewBox="0 0 540 360"
      className="w-full rounded-2xl border border-ink-200 bg-white shadow-soft"
      role="img"
      aria-label={t('hero.mock_signals_aria')}
    >
      <rect width="540" height="360" rx="14" fill="white" />
      <rect width="540" height="44" fill="#FAFAFA" />
      <circle cx="20" cy="22" r="5" fill="#0A5BFF" />
      <text x="34" y="27" fontFamily="Inter, system-ui" fontSize="13" fill="#0E1116" fontWeight="600">
        Signal feed · Tokyo Sales
      </text>
      <text x="520" y="27" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#6B7280" textAnchor="end">
        ja-JP · ¥
      </text>

      {[
        { y: 64, dot: '#10B981', kind: 'FUND', t: '資金調達 · ¥1.2B Series B — TechRise', s: 'Lead: SoftBank · 法人番号 1234567890123 · 2 contacts' },
        { y: 134, dot: '#E11D48', kind: 'TDN', t: 'TDNet 適時開示 · 業績予想の上方修正', s: 'ヤマト商事 · 証券コード→法人番号で自動解決' },
        { y: 204, dot: '#F59E0B', kind: 'JOB', t: '人事異動 · 田中様 → 営業本部長', s: '前職: SDR Manager · 相互の知人 1名' },
      ].map((r) => (
        <g key={r.y} transform={`translate(20, ${r.y})`}>
          <rect width="500" height="56" rx="9" fill={r.dot === '#10B981' ? '#F5FBF8' : '#FFFFFF'} stroke="#E5E7EB" />
          <rect x="0" y="0" width="3" height="56" rx="1.5" fill={r.dot} />
          <text x="18" y="22" fontFamily="JetBrains Mono, monospace" fontSize="9" fontWeight="700" fill={r.dot} letterSpacing="1">
            {r.kind}
          </text>
          <text x="58" y="24" fontFamily="Noto Sans JP, system-ui" fontSize="12" fill="#0E1116" fontWeight="600">
            {r.t}
          </text>
          <text x="58" y="42" fontFamily="Noto Sans JP, system-ui" fontSize="10" fill="#6B7280">
            {r.s}
          </text>
        </g>
      ))}
      <text x="20" y="296" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#9CA3AF">
        TDNet: live · EDINET: 設定後に有効
      </text>
      <text x="20" y="330" fontFamily="Noto Sans JP, system-ui" fontSize="10" fill="#0A5BFF" fontWeight="600">
        → Compose · 敬語で下書き（送信は人の承認）
      </text>
    </svg>
  );
}

/** Keigo composer face — register + drafted body + cost/citation stamp. */
function KeigoFace() {
  const { t } = useTranslation();
  return (
    <svg
      viewBox="0 0 540 360"
      className="w-full rounded-2xl border border-ink-200 bg-white shadow-soft"
      role="img"
      aria-label={t('hero.mock_keigo_aria')}
    >
      <rect width="540" height="360" rx="14" fill="white" />
      <rect width="540" height="44" fill="#FAFAFA" />
      <text x="20" y="27" fontFamily="Inter, system-ui" fontSize="13" fill="#0E1116" fontWeight="600">
        Keigo Composer
      </text>
      <g transform="translate(360, 13)">
        <rect width="160" height="20" rx="10" fill="#EEF3FF" />
        <text x="80" y="14" fontFamily="Noto Sans JP, system-ui" fontSize="10" fill="#0A5BFF" fontWeight="600" textAnchor="middle">
          register · 紹介経由
        </text>
      </g>

      <g transform="translate(22, 70)" fontFamily="Noto Sans JP, system-ui" fill="#0E1116">
        <text y="0" fontSize="12" fontWeight="700">件名: ご紹介を頂きました桜田商事の田中と申します</text>
        <text y="34" fontSize="11" fill="#374151">山田 太郎 様</text>
        <text y="56" fontSize="11" fill="#374151">いつもお世話になっております。先日、佐藤様より</text>
        <text y="76" fontSize="11" fill="#374151">ご紹介を賜りました、桜田商事株式会社の田中と</text>
        <text y="96" fontSize="11" fill="#374151">申します。貴社の Series B 調達のニュースを拝見し、</text>
        <text y="116" fontSize="11" fill="#374151">弊社のシグナル基盤がお役に立てればと存じます。</text>
      </g>

      <g transform="translate(22, 300)">
        <text y="0" fontFamily="JetBrains Mono, monospace" fontSize="10" fill="#6B7280">
          model · ¥0.42 · cites: signal #871, contact #5023
        </text>
        <text y="22" fontFamily="Noto Sans JP, system-ui" fontSize="10" fill="#0A5BFF" fontWeight="600">
          送信は、必ずあなたの承認で。
        </text>
      </g>
    </svg>
  );
}
